import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, ArrowRight, User, Users, Heart, DollarSign } from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Your name', icon: User },
  { id: 2, title: 'Travel group', icon: Users },
  { id: 3, title: 'Travel style', icon: Heart },
  { id: 4, title: 'Interests', icon: Heart },
  { id: 5, title: 'Currency', icon: DollarSign }
];

const INTERESTS = [
  'Beach', 'City', 'Nature', 'Culture', 'Food', 'Nightlife',
  'Shopping', 'Wellness', 'Adventure', 'Museums', 'Photography'
];

const CURRENCIES = [
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' }
];

export default function OnboardingPage({ onComplete, onSkip }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [profile, setProfile] = useState({
    name: '',
    travelGroup: '',
    travelStyle: '',
    interests: [],
    currency: 'IDR'
  });

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(profile);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleInterestToggle = (interest) => {
    const lowerInterest = interest.toLowerCase();
    if (profile.interests.includes(lowerInterest)) {
      setProfile({
        ...profile,
        interests: profile.interests.filter(i => i !== lowerInterest)
      });
    } else {
      setProfile({
        ...profile,
        interests: [...profile.interests, lowerInterest]
      });
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return profile.name.trim().length > 0;
      case 2: return profile.travelGroup.length > 0;
      case 3: return profile.travelStyle.length > 0;
      case 4: return profile.interests.length > 0;
      case 5: return profile.currency.length > 0;
      default: return false;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ backgroundColor: 'var(--bg)' }}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text)' }}>
            Let's get to know you! 👋
          </h1>
          <p style={{ color: 'var(--muted)' }}>
            Quick questions to personalize your trips
          </p>
          <button
            onClick={onSkip}
            className="text-sm underline mt-3 transition-colors hover:opacity-80"
            style={{ color: 'var(--muted)' }}
          >
            Skip for now
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-3 gap-1.5">
            {STEPS.map((step, index) => (
              <div
                key={step.id}
                className="flex-1 h-2 rounded-full transition-all duration-300"
                style={{ 
                  backgroundColor: index + 1 <= currentStep ? 'var(--coral)' : 'var(--border-color)' 
                }}
              />
            ))}
          </div>
          <p className="text-sm text-center" style={{ color: 'var(--muted)' }}>
            Step {currentStep} of {STEPS.length}
          </p>
        </div>

        {/* Main Card */}
        <Card className="swoopy-card p-6 md:p-8 animate-fade-in">
          {/* Step 1: Name */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="icon-bubble w-14 h-14 mx-auto mb-4">
                  <User className="w-7 h-7" style={{ color: 'var(--coral)' }} />
                </div>
                <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text)' }}>What should we call you?</h2>
                <p style={{ color: 'var(--muted)' }}>We'll use this to make things personal ✨</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name" style={{ color: 'var(--text)' }}>Your name</Label>
                <Input
                  id="name"
                  className="swoopy-input"
                  placeholder="e.g., Alex"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* Step 2: Travel Group */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="icon-bubble w-14 h-14 mx-auto mb-4">
                  <Users className="w-7 h-7" style={{ color: 'var(--coral)' }} />
                </div>
                <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text)' }}>Who's coming along?</h2>
                <p style={{ color: 'var(--muted)' }}>This helps us pick the right activities 🎯</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'solo', label: 'Just me', emoji: '🧳' },
                  { value: 'couple', label: 'My person', emoji: '❤️' },
                  { value: 'friends', label: 'The squad', emoji: '👯' },
                  { value: 'family', label: 'Family', emoji: '👨‍👩‍👧‍👦' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setProfile({ ...profile, travelGroup: option.value })}
                    className="p-5 rounded-2xl border-2 transition-all text-center hover:-translate-y-1"
                    style={{
                      borderColor: profile.travelGroup === option.value ? 'var(--coral)' : 'var(--border-color)',
                      backgroundColor: profile.travelGroup === option.value ? 'var(--coral-soft)' : 'var(--card)'
                    }}
                  >
                    <div className="text-3xl mb-2">{option.emoji}</div>
                    <div className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{option.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Travel Style */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="icon-bubble w-14 h-14 mx-auto mb-4">
                  <Heart className="w-7 h-7" style={{ color: 'var(--coral)' }} />
                </div>
                <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text)' }}>What's your travel vibe?</h2>
                <p style={{ color: 'var(--muted)' }}>No wrong answers here! 💫</p>
              </div>
              <div className="space-y-3">
                {[
                  { value: 'budget', label: 'Budget Explorer', desc: 'Hostels, street food, local transport' },
                  { value: 'mid-range', label: 'Comfort Cruiser', desc: 'Nice hotels, good restaurants, some splurges' },
                  { value: 'luxury', label: 'Treat Yourself', desc: 'Premium stays, fine dining, VIP experiences' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setProfile({ ...profile, travelStyle: option.value })}
                    className="w-full p-4 rounded-2xl border-2 transition-all text-left hover:-translate-y-0.5"
                    style={{
                      borderColor: profile.travelStyle === option.value ? 'var(--coral)' : 'var(--border-color)',
                      backgroundColor: profile.travelStyle === option.value ? 'var(--coral-soft)' : 'var(--card)'
                    }}
                  >
                    <div className="font-semibold mb-1" style={{ color: 'var(--text)' }}>{option.label}</div>
                    <div className="text-sm" style={{ color: 'var(--muted)' }}>{option.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Interests */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="icon-bubble w-14 h-14 mx-auto mb-4">
                  <Heart className="w-7 h-7" style={{ color: 'var(--coral)' }} />
                </div>
                <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text)' }}>What gets you excited?</h2>
                <p style={{ color: 'var(--muted)' }}>Pick all that spark joy ✨</p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {INTERESTS.map((interest) => (
                  <Badge
                    key={interest}
                    className="px-4 py-2 cursor-pointer text-sm transition-all rounded-full"
                    style={{
                      backgroundColor: profile.interests.includes(interest.toLowerCase()) ? 'var(--coral)' : 'var(--coral-soft)',
                      color: profile.interests.includes(interest.toLowerCase()) ? 'white' : 'var(--coral)',
                      border: 'none'
                    }}
                    onClick={() => handleInterestToggle(interest)}
                  >
                    {interest}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Currency */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="icon-bubble w-14 h-14 mx-auto mb-4">
                  <DollarSign className="w-7 h-7" style={{ color: 'var(--coral)' }} />
                </div>
                <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text)' }}>Almost there! 🎉</h2>
                <p style={{ color: 'var(--muted)' }}>Which currency should we show prices in?</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency" style={{ color: 'var(--text)' }}>Your currency</Label>
                <Select value={profile.currency} onValueChange={(value) => setProfile({ ...profile, currency: value })}>
                  <SelectTrigger className="swoopy-input h-14">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((currency) => (
                      <SelectItem key={currency.code} value={currency.code}>
                        {currency.symbol} {currency.name} ({currency.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6" style={{ borderTop: '1px solid var(--border-color)' }}>
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="rounded-full"
              style={{ color: 'var(--muted)' }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="swoopy-btn px-6 h-11 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentStep === STEPS.length ? "Let's go!" : 'Next'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
